import { 
    PhoneIcon,
    ClockIcon,
    ExclamationCircleIcon,
    UserCircleIcon,
    ChatBubbleBottomCenterTextIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

const ConversationsList = ({ conversations, selectedPhone, onSelectConversation, loading }) => {
    
    const formatTime = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);

        if (diffMins < 60) {
            return `Hace ${diffMins} min`;
        } else if (diffHours < 24) {
            return `Hace ${diffHours}h`;
        } else {
            return date.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
        }
    };

    const formatPhone = (phone) => {
        // Formato: +52 123 456 7890
        if (phone.startsWith('521') && phone.length === 13) {
            return `+52 ${phone.slice(3, 6)} ${phone.slice(6, 9)} ${phone.slice(9)}`;
        }
        return phone;
    };

    const getTimeRemaining = (expiresAt) => {
        const now = new Date();
        const expires = new Date(expiresAt);
        const diffMs = expires - now;
        const diffHours = Math.floor(diffMs / 3600000);
        const diffMins = Math.floor((diffMs % 3600000) / 60000);

        if (diffHours > 0) {
            return `${diffHours}h ${diffMins}m`;
        } else if (diffMins > 0) {
            return `${diffMins}m`;
        } else {
            return 'Expirada';
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#25D366]"></div>
            </div>
        );
    }

    if (!conversations || conversations.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 px-4">
                <ChatBubbleBottomCenterTextIcon className="h-16 w-16 mb-3" />
                <p className="text-sm text-center">No hay conversaciones activas</p>
                <p className="text-xs text-center mt-1">El historial se muestra hasta 42 horas después del último mensaje</p>
            </div>
        );
    }

    return (
        <div className="h-full overflow-y-auto" style={{height: '100%', overflowY: 'scroll'}}>
            {conversations.map((conversation) => {
                const isSelected = selectedPhone === conversation.phone_number;
                const isExpiringSoon = new Date(conversation.expires_at) - new Date() < 3600000; // < 1 hora

                return (
                    <div
                        key={conversation.phone_number}
                        onClick={() => onSelectConversation(conversation.phone_number)}
                        className={`
                            flex items-start gap-3 p-1 border-b border-blue-400 cursor-pointer transition-colors
                            ${isSelected ? 'bg-[#F0F2F5]' : 'hover:bg-gray-50'}
                        `}
                    >
                        {/* Avatar */}
                        <div className="shrink-0 p-2 pt-8">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#11cdef] to-[#3862ee] flex items-center justify-center">
                                <UserCircleIcon className="h-8 w-8 text-white" />
                            </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                            {/* Name & Time */}
                            <div className="flex items-start justify-between gap-2 mb-1">
                                <p className="font-semibold text-gray-900 text-sm truncate">
                                    {conversation.patient_name}
                                </p>
                                <span className="text-xs text-gray-500 shrink-0">
                                    {formatTime(conversation.last_message_time)}
                                </span>
                            </div>

                            {/* Phone */}
                            <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
                                <PhoneIcon className="h-3.5 w-3.5" />
                                <span className="font-mono">{formatPhone(conversation.phone_number)}</span>
                            </div>

                            {/* Last Message */}
                            <p className="text-sm text-gray-600 truncate mb-0">
                                {conversation.last_message}
                            </p>

                            {/* Status & Time Remaining */}
                            <div className="flex items-center justify-between">
                                {/* Needs Response Badge */}
                                {conversation.needs_response ? (
                                    <div className="flex items-center gap-1 text-xs font-medium text-orange-600">
                                        <ExclamationCircleIcon className="h-4 w-4" />
                                        <span>Sin responder</span>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-1 text-xs font-medium text-green-600">
                                        <CheckCircleIcon className="h-4 w-4" />
                                        <span>Respondido</span>
                                    </div>
                                )}

                                {/* Time Remaining */}
                                <div className={`
                                    flex items-center gap-1 text-xs
                                    ${isExpiringSoon ? 'text-red-500 font-medium' : 'text-gray-500'}
                                `}>
                                    <ClockIcon className="h-3.5 w-3.5" />
                                    <span>{getTimeRemaining(conversation.expires_at)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default ConversationsList;
